'use client';

import { memo, type FC } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { classes } from '@/next-utils/css-utils/scss-utils';
import type { IDrawerProps } from '../types';
import { useDrawerActions } from '../hooks';
import { DrawerPortal } from './DrawerPortal';

import S from './styles.module.scss';

/**
 * A drawer is an overlay, that slides into the view and is displayed above other content. The DrawerInternal component is the internal component
 * that creates the portal to the document.body. The portal cannot be created repeatedly and instead if removed the whole component is unmounted.
 * This component itself is a wrapper for the internals of the portal, which optionally render based on the showCurtain prop.
 *
 * @throws Error if used outside of a DrawersProvider.
 */
export const DrawerInternal: FC<IDrawerProps> = memo(
  ({
    open,
    children,
    fromLeft = false,
    'aria-label': ariaLabel,
    rootClassName = '',
    showCurtain = false,
    curtainClassName = '',
    drawerClassName = '',
    usePortal = true
  }) => {
    const { closeAllDrawers } = useDrawerActions();

    const component = (
      <AnimatePresence>
        <motion.div
          className={classes(S.root, rootClassName, !open && S.close)}
          role="dialog"
          aria-label={ariaLabel}
          initial={{ opacity: 0 }}
          animate={{ opacity: open ? 1 : 0 }}
          exit={{ opacity: 0 }}
        >
          <DrawerPortal
            showCurtain={showCurtain}
            closeAllDrawers={closeAllDrawers}
            curtainClassName={curtainClassName}
          />
          <motion.div
            className={drawerClassName}
            tabIndex={-1}
            data-draw-from={fromLeft ? 'left' : 'right'}
            initial={{ x: fromLeft ? '-100%' : '100%' }}
            transition={{ type: 'tween' }}
            animate={{ x: open ? '0%' : fromLeft ? '-100%' : '100%' }}
            exit={{ x: fromLeft ? '-100%' : '100%' }}
          >
            {children}
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );

    if (usePortal) {
      return <>{createPortal(component, document.body)}</>;
    }

    return component;
  }
);
